// src/components/Disclosure.jsx
import React from "react";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Stack,
  Grid,
  Chip,
  Link as MLink,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GavelIcon from "@mui/icons-material/Gavel";
import InfoIcon from "@mui/icons-material/Info";
import PublicIcon from "@mui/icons-material/Public";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import ShieldIcon from "@mui/icons-material/Shield";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ArticleIcon from "@mui/icons-material/Article";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import SummarizeIcon from "@mui/icons-material/Summarize";

// Optional SEO hooks (align with your SEO utilities)
import { useSEO, useJsonLD } from "./SEO"; // adjust path if needed

// ---- Theme tokens (dark + Binance-like accent)
const ACCENT = "#F0B90B";
const BG = "#263238";
const SURFACE = "rgba(38,50,56,0.85)";
const BORDER = "rgba(255,255,255,0.08)";
const TEXT = "#EAEFF2";
const MUTED = "#B0BEC5";

function SectionTitle({ icon, title, id }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      id={id}
      sx={{ scrollMarginTop: 96 }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "999px",
          bgcolor: "rgba(255,255,255,0.06)",
          border: `1px solid ${BORDER}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: ACCENT,
          "& svg": { fontSize: 22 },
          boxShadow: "0 0 0 2px rgba(240,185,11,0.12)",
          position: "relative",
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(240,185,11,0.20), transparent 45%)",
          },
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" sx={{ color: TEXT, fontWeight: 900 }}>
        {title}
      </Typography>
    </Stack>
  );
}

function KVP({ label, value }) {
  return (
    <Stack spacing={0.25}>
      <Typography sx={{ color: MUTED, fontSize: 12 }}>{label}</Typography>
      <Typography sx={{ color: TEXT, fontWeight: 800 }}>{value}</Typography>
    </Stack>
  );
}

export default function Disclosure() {
  // ---------- SEO ----------
  useSEO?.({
    title: "Disclosures — 1CryptoX | Risk & Legal Notices",
    description:
      "Read important risk disclosures, eligibility, regional restrictions, non-advice notice, and platform disclaimers. 1CryptoX encourages responsible participation.",
    canonical: "https://www.1cryptox.com/disclosures",
    keywords: [
      "disclosure",
      "risk warning",
      "eligibility",
      "kyc",
      "regional restrictions",
      "outages maintenance",
      "not financial advice",
      "privacy summary",
      "tax notice",
      "fees overview",
    ],
    og: {
      title: "Disclosures — 1CryptoX",
      description:
        "Important disclosures about risk, eligibility, restrictions, and legal notices.",
      url: "https://www.1cryptox.com/disclosures",
      image: "https://www.1cryptox.com/og/disclosures-card.jpg",
      siteName: "1CryptoX",
      type: "website",
    },
    twitter: {
      card: "summary_large_image",
      title: "Disclosures — 1CryptoX",
      description:
        "Risk disclosures, eligibility, restrictions, and legal notices.",
      image: "https://www.1cryptox.com/og/disclosures-card.jpg",
    },
  });

  useJsonLD?.("disclosures-page", {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Disclosures — 1CryptoX",
    url: "https://www.1cryptox.com/disclosures",
    description:
      "Important disclosures covering risk, eligibility, and legal notices for users.",
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.1cryptox.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Disclosures",
          item: "https://www.1cryptox.com/disclosures",
        },
      ],
    },
  });

  const updatedAt = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        px: { xs: 2, md: 4 },
        py: { xs: 4, md: 6 },
        position: "relative",
        overflow: "hidden",
        background:
          "radial-gradient(60% 60% at 70% 30%, rgba(240,185,11,0.10) 0%, rgba(240,185,11,0.03) 100%)",
        bgcolor: "#0B0E11",
      }}
    >
      <Container maxWidth="lg" disableGutters>
        {/* HERO */}
        <Card
          sx={{
            bgcolor: SURFACE,
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
            backdropFilter: "blur(8px)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(70% 60% at 80% 20%, rgba(240,185,11,0.12) 0%, rgba(240,185,11,0.02) 100%)",
              pointerEvents: "none",
            }}
          />
          <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
            <Stack spacing={1}>
              <Typography
                variant="overline"
                sx={{ color: ACCENT, letterSpacing: 1, fontWeight: 900 }}
              >
                Legal & Risk
              </Typography>
              <Typography variant="h3" sx={{ color: TEXT, fontWeight: 900 }}>
                Disclosures & Important Notices
              </Typography>
              <Typography sx={{ color: MUTED, maxWidth: 920 }}>
                The following disclosures summarize essential information for
                users. These high-level statements are for general awareness and
                do not replace the full{" "}
                <MLink
                  href="/terms"
                  underline="hover"
                  sx={{ color: ACCENT, fontWeight: 800 }}
                >
                  Terms of Use
                </MLink>{" "}
                and{" "}
                <MLink
                  href="/privacy"
                  underline="hover"
                  sx={{ color: ACCENT, fontWeight: 800 }}
                >
                  Privacy Policy
                </MLink>
                .
              </Typography>

              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                <Chip
                  icon={<GavelIcon />}
                  label="Not financial advice"
                  sx={{
                    bgcolor: "rgba(240,185,11,0.12)",
                    color: ACCENT,
                    border: `1px solid rgba(240,185,11,0.25)`,
                    fontWeight: 700,
                  }}
                />
                <Chip
                  icon={<VerifiedUserIcon />}
                  label="Eligibility & KYC"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.06)",
                    color: TEXT,
                    border: `1px solid ${BORDER}`,
                  }}
                />
                <Chip
                  icon={<PublicIcon />}
                  label="Regional restrictions"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.06)",
                    color: TEXT,
                    border: `1px solid ${BORDER}`,
                  }}
                />
              </Stack>

              <Divider sx={{ my: 1.5, borderColor: BORDER }} />

              {/* Quick meta */}
              <Grid container spacing={2}>
                <Grid item xs={6} md="auto">
                  <KVP label="Document" value="Disclosures" />
                </Grid>
                <Grid item xs={6} md="auto">
                  <KVP label="Last updated" value={updatedAt} />
                </Grid>
                <Grid item xs={12} md>
                  <Alert
                    icon={<InfoIcon />}
                    severity="info"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.06)",
                      color: TEXT,
                      border: `1px solid ${BORDER}`,
                      "& .MuiAlert-icon": { color: ACCENT },
                    }}
                  >
                    These statements are high-level and may be updated. Please
                    review them periodically alongside the full policies.
                  </Alert>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        {/* TOC */}
        <Card sx={{ mt: 2, bgcolor: BG, border: `1px solid ${BORDER}` }}>
          <CardContent>
            <Typography sx={{ color: TEXT, fontWeight: 900, mb: 1 }}>
              Contents
            </Typography>
            <Grid container spacing={1.5}>
              {[
                ["risk", "General Risk Notice"],
                ["advice", "No Financial/Investment Advice"],
                ["regional", "Regional Restrictions"],
                ["eligibility", "Eligibility & KYC"],
                ["fees", "Fees, Rewards & Costs"],
                ["market", "Market & Volatility"],
                ["trading", "Trading-Specific Risks"],
                ["ai", "AI-Related Disclaimers"],
                ["privacy", "Data & Privacy Summary"],
                ["outages", "Outages & Maintenance"],
                ["links", "Third-Party Links"],
                ["performance", "Past Performance"],
                ["tax", "Taxes & Reporting"],
                ["contact", "Contact & Support"],
              ].map(([id, label]) => (
                <Grid key={id} item xs={12} sm={6} md={4}>
                  <MLink
                    href={`#${id}`}
                    underline="hover"
                    sx={{ color: ACCENT, fontWeight: 800 }}
                  >
                    {label}
                  </MLink>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* SECTIONS */}
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
            <CardContent>
              <SectionTitle
                id="risk"
                icon={<WarningAmberIcon />}
                title="General Risk Notice"
              />
              <Typography sx={{ color: MUTED, mt: 1 }}>
                Participation involves risk. Prices may fluctuate significantly
                and can become illiquid or volatile. Only engage with amounts
                you can afford to tolerate. Ensure you understand your own risk
                tolerance, and consider independent professional advice where
                appropriate.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
            <CardContent>
              <SectionTitle
                id="advice"
                icon={<HistoryEduIcon />}
                title="No Financial/Investment Advice"
              />
              <Typography sx={{ color: MUTED, mt: 1 }}>
                Information, visuals, or features are provided for general
                awareness and user experience. Nothing presented constitutes
                investment, tax, legal, or accounting advice. You remain
                responsible for all decisions and outcomes.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
            <CardContent>
              <SectionTitle
                id="regional"
                icon={<PublicIcon />}
                title="Regional Restrictions"
              />
              <Typography sx={{ color: MUTED, mt: 1 }}>
                Access may be limited by jurisdictional rules or local
                requirements. Availability of features, onboarding, or supported
                assets can vary by region and may be updated at any time without
                prior notice.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
            <CardContent>
              <SectionTitle
                id="eligibility"
                icon={<VerifiedUserIcon />}
                title="Eligibility & KYC"
              />
              <Typography sx={{ color: MUTED, mt: 1 }}>
                Participation may require identity verification and other
                checks. Providing accurate information is essential for
                eligibility and continued access. Failure to meet requirements
                may limit usage or lead to restrictions.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
            <CardContent>
              <SectionTitle
                id="fees"
                icon={<AccountBalanceIcon />}
                title="Fees, Rewards & Costs"
              />
              <Typography sx={{ color: MUTED, mt: 1 }}>
                Fee structures, rewards, and incentives—where available—are
                subject to eligibility, limits, and updates. Any promotional
                benefits may be time-bound and governed by separate terms.
                Review the{" "}
                <MLink
                  href="/fees"
                  underline="hover"
                  sx={{ color: ACCENT, fontWeight: 800 }}
                >
                  Fees
                </MLink>{" "}
                page for the most current, high-level overview.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
            <CardContent>
              <SectionTitle
                id="market"
                icon={<TrendingDownIcon />}
                title="Market & Volatility"
              />
              <Typography sx={{ color: MUTED, mt: 1 }}>
                Markets can be rapidly moving and sensitive to external events.
                Liquidity, spreads, execution, and availability are not
                guaranteed. Your outcomes may differ from indicative information
                or historical references.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
            <CardContent>
              <SectionTitle
                id="trading"
                icon={<QueryStatsIcon />}
                title="Trading-Specific Risks"
              />
              <Typography sx={{ color: MUTED, mt: 1 }}>
                Placing orders involves execution uncertainty. Delays, slippage,
                partial fills, or cancellations may occur. Use caution with
                advanced order types and leverage-like exposures; adverse
                movements can amplify outcomes.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
            <CardContent>
              <SectionTitle
                id="ai"
                icon={<IntegrationInstructionsIcon />}
                title="AI-Related Disclaimers"
              />
              <Typography sx={{ color: MUTED, mt: 1 }}>
                Any AI-assisted experiences are high-level, exploratory features
                intended to enhance usability and workflow. They are not
                recommendations or guarantees. Outputs may be incomplete or
                imprecise and should be evaluated independently before use.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
            <CardContent>
              <SectionTitle
                id="privacy"
                icon={<ShieldIcon />}
                title="Data & Privacy Summary"
              />
              <Typography sx={{ color: MUTED, mt: 1 }}>
                Data handling follows general principles of necessity,
                proportionality, and security. Review the{" "}
                <MLink
                  href="/privacy"
                  underline="hover"
                  sx={{ color: ACCENT, fontWeight: 800 }}
                >
                  Privacy Policy
                </MLink>{" "}
                for details about categories, purposes, retention, and rights.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
            <CardContent>
              <SectionTitle
                id="outages"
                icon={<FlashOnIcon />}
                title="Outages, Maintenance & Availability"
              />
              <Typography sx={{ color: MUTED, mt: 1 }}>
                Access may be interrupted due to maintenance, updates, provider
                dependencies, or unforeseen events. We strive for continuity,
                but availability is not guaranteed. Check{" "}
                <MLink
                  href="/status"
                  underline="hover"
                  sx={{ color: ACCENT, fontWeight: 800 }}
                >
                  Status
                </MLink>{" "}
                for high-level updates.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
            <CardContent>
              <SectionTitle
                id="links"
                icon={<LinkOffIcon />}
                title="Third-Party Links & References"
              />
              <Typography sx={{ color: MUTED, mt: 1 }}>
                External links or references are provided for convenience and do
                not imply endorsement. We are not responsible for third-party
                content or policies; review their terms independently.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
            <CardContent>
              <SectionTitle
                id="performance"
                icon={<ArticleIcon />}
                title="Past Performance"
              />
              <Typography sx={{ color: MUTED, mt: 1 }}>
                Past performance does not guarantee future results.
                Illustrations or examples are for context only and may not
                reflect your outcomes.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
            <CardContent>
              <SectionTitle
                id="tax"
                icon={<SummarizeIcon />}
                title="Taxes & Reporting"
              />
              <Typography sx={{ color: MUTED, mt: 1 }}>
                You are responsible for understanding and meeting your personal
                tax obligations. Consider consulting a qualified professional in
                your jurisdiction. For general references, see{" "}
                <MLink
                  href="/help-center"
                  underline="hover"
                  sx={{ color: ACCENT, fontWeight: 800 }}
                >
                  Help Center
                </MLink>
                .
              </Typography>
            </CardContent>
          </Card>

          {/* FAQ (optional, professional tone) */}
          <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
            <CardContent>
              <SectionTitle
                icon={<SupportAgentIcon />}
                title="FAQs"
                id="faqs"
              />
              <Accordion
                disableGutters
                sx={{
                  bgcolor: "transparent",
                  border: "none",
                  "&:before": { display: "none" },
                  borderTop: `1px solid ${BORDER}`,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: ACCENT }} />}
                >
                  <Typography sx={{ color: TEXT, fontWeight: 800 }}>
                    Are these disclosures exhaustive?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography sx={{ color: MUTED }}>
                    No. They provide a concise, high-level overview. The full
                    Terms and Privacy Policy remain the primary references and
                    may be updated.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters
                sx={{
                  bgcolor: "transparent",
                  border: "none",
                  "&:before": { display: "none" },
                  borderTop: `1px solid ${BORDER}`,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: ACCENT }} />}
                >
                  <Typography sx={{ color: TEXT, fontWeight: 800 }}>
                    How can I get assistance?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography sx={{ color: MUTED }}>
                    Visit the{" "}
                    <MLink
                      href="/support"
                      underline="hover"
                      sx={{ color: ACCENT, fontWeight: 800 }}
                    >
                      Support
                    </MLink>{" "}
                    section for general guidance. For account-specific
                    inquiries, our team may request verification.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        </Stack>

        {/* CTA */}
        <Box sx={{ mt: 2 }}>
          <Card
            sx={{
              bgcolor: BG,
              border: `1px solid ${BORDER}`,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(240,185,11,0.05), rgba(240,185,11,0.00))",
                pointerEvents: "none",
              }}
            />
            <CardContent
              sx={{
                p: { xs: 2, md: 2.5 },
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Stack spacing={0.5}>
                <Typography sx={{ color: TEXT, fontWeight: 900 }}>
                  Need more details?
                </Typography>
                <Typography sx={{ color: MUTED }}>
                  Explore the full{" "}
                  <MLink
                    href="/terms"
                    underline="hover"
                    sx={{ color: ACCENT, fontWeight: 800 }}
                  >
                    Terms of Use
                  </MLink>{" "}
                  and{" "}
                  <MLink
                    href="/privacy"
                    underline="hover"
                    sx={{ color: ACCENT, fontWeight: 800 }}
                  >
                    Privacy Policy
                  </MLink>
                  , or contact Support for further assistance.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  href="/terms"
                  variant="outlined"
                  sx={{
                    color: TEXT,
                    borderColor: BORDER,
                    textTransform: "none",
                    fontWeight: 800,
                  }}
                >
                  Terms
                </Button>
                <Button
                  href="/support"
                  variant="contained"
                  sx={{
                    bgcolor: ACCENT,
                    color: "#1C2526",
                    fontWeight: 900,
                    textTransform: "none",
                    px: 2.25,
                    "&:hover": { bgcolor: "#FFD54F" },
                  }}
                >
                  Contact Support
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
}
